import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Briefcase, Calendar, MapPin, Tag, CheckCircle2, AlertTriangle, FileText, ExternalLink, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import CazarFacturaDialog from "@/components/CazarFacturaDialog";

const ESTADO_BADGE = {
  montado: { label: "✅ Montado", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  semi_montado: { label: "⚠️ Semi", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  incidencia: { label: "🚨 Incidencia", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  cancelado: { label: "❌ Cancelado", cls: "bg-zinc-500/20 text-zinc-300 border-zinc-500/30" },
};

/**
 * Modal con todos los trabajos (paradas) realizados por un colaborador externo.
 *
 * Props:
 *  - colaborador: { id, nombre, autonomo } (puede ser null para cerrar)
 *  - onClose: () => void
 */
export default function ColaboradorTrabajosDialog({ colaborador, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [openCazar, setOpenCazar] = useState(false);
  const [facturasCazadas, setFacturasCazadas] = useState([]);
  const [loadingFC, setLoadingFC] = useState(false);

  const cargar = async () => {
    if (!colaborador?.id) return;
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data: res } = await api.get(`/colaboradores/${colaborador.id}/trabajos`, { params });
      setData(res);
    } catch (e) {
      setData({ trabajos: [], error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const cargarFacturasCazadas = async () => {
    if (!colaborador?.id) return;
    setLoadingFC(true);
    try {
      const { data: res } = await api.get(`/colaboradores/${colaborador.id}/facturas-cazadas`);
      setFacturasCazadas(res.facturas || []);
    } catch (e) {
      setFacturasCazadas([]);
    } finally {
      setLoadingFC(false);
    }
  };

  const eliminarFactura = async (fid) => {
    if (!window.confirm("¿Eliminar este registro? Las paradas dejarán de estar marcadas como facturadas.")) return;
    try {
      await api.delete(`/colaboradores/${colaborador.id}/facturas-cazadas/${fid}`);
      cargarFacturasCazadas();
      cargar(); // refrescar trabajos para que se actualice estado facturada
    } catch (e) {
      alert("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    if (colaborador?.id) {
      cargar();
      cargarFacturasCazadas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colaborador?.id]);

  if (!colaborador) return null;

  return (
    <Dialog open={!!colaborador} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-amber-400" />
            Trabajos de {colaborador.nombre}
            {colaborador.autonomo
              ? <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30">🧑‍💼 Autónomo</span>
              : <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">🏢 Externo</span>
            }
          </DialogTitle>
        </DialogHeader>

        {/* Filtros fecha */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Periodo:</span>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            placeholder="desde" className="bg-zinc-900 border-zinc-800 h-9 w-40 text-xs"
            data-testid="trabajos-fecha-desde" />
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            placeholder="hasta" className="bg-zinc-900 border-zinc-800 h-9 w-40 text-xs"
            data-testid="trabajos-fecha-hasta" />
          <Button onClick={cargar} size="sm" className="h-9 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
            data-testid="trabajos-filter-apply">
            Aplicar
          </Button>
          {(desde || hasta) && (
            <Button variant="ghost" size="sm" onClick={() => { setDesde(""); setHasta(""); setTimeout(cargar, 50); }}
              className="h-9 text-xs text-zinc-400">
              Limpiar
            </Button>
          )}
        </div>

        {/* Resumen */}
        {data && (
          <div className="grid grid-cols-4 gap-2 mb-4 text-center">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-xl font-bold text-amber-300">{data.rutas_count}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Rutas</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-xl font-bold text-zinc-100">{data.total_paradas}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Paradas</div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2">
              <div className="text-xl font-bold text-emerald-300">{data.paradas_montadas}</div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-400/80">Montadas</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
              <div className="text-xl font-bold text-red-300">{data.paradas_incidencia}</div>
              <div className="text-[10px] uppercase tracking-wider text-red-400/80">Incidencias</div>
            </div>
          </div>
        )}

        {/* Lista trabajos */}
        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando trabajos…
          </div>
        )}
        {!loading && data && data.trabajos.length === 0 && (
          <div className="text-center py-12 text-zinc-500 text-sm">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Sin trabajos registrados {(desde || hasta) ? "en este periodo" : "todavía"}.
          </div>
        )}
        {!loading && data && data.trabajos.length > 0 && (
          <div className="border border-zinc-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/80">
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">PDV / Espacio</th>
                  <th className="px-3 py-2">Petición</th>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {data.trabajos.map((t, i) => {
                  const est = ESTADO_BADGE[t.estado_cierre] || null;
                  return (
                    <tr key={`${t.ruta_id}-${t.parada_idx}-${i}`}
                      data-testid={`trabajo-row-${i}`}
                      className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                      <td className="px-3 py-2 whitespace-nowrap text-zinc-300">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-zinc-500" />
                          {t.fecha ? new Date(t.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono">{t.ruta_codigo}</div>
                      </td>
                      <td className="px-3 py-2 max-w-xs">
                        <div className="flex items-center gap-1 text-zinc-100">
                          <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                          <span className="truncate">{t.espacio_nombre || "—"}</span>
                        </div>
                        <div className="text-[10px] text-zinc-500 flex items-center gap-1">
                          {t.cadena_nombre && <Tag className="w-2.5 h-2.5" />}
                          {t.cadena_nombre} {t.espacio_ciudad && `· ${t.espacio_ciudad}`}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-amber-300 font-mono text-xs">{t.peticion_codigo || "—"}</div>
                        <div className="text-[10px] text-zinc-500 truncate max-w-[140px]">{t.peticion_nombre}</div>
                      </td>
                      <td className="px-3 py-2 text-zinc-300 max-w-xs truncate" title={t.linea_descripcion}>
                        {t.linea_descripcion || "—"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {est ? (
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${est.cls}`}>{est.label}</span>
                        ) : (
                          <span className="text-[10px] text-zinc-500 italic">Sin cierre</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {t.peticion_id && (
                          <Link to={`/peticiones/${t.peticion_id}`}
                            data-testid={`open-peticion-${t.peticion_codigo}`}
                            className="inline-flex items-center text-zinc-500 hover:text-amber-300">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Info: factura cruzada — ACTIVO */}
        <div className="mt-4 bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-violet-400 shrink-0" />
          <div className="flex-1 text-xs text-violet-100/90">
            <b>Cruzar factura ↔ trabajos:</b> sube la factura emitida por este colaborador y la IA detectará
            automáticamente qué trabajos corresponden a cada línea (por fecha y nombre del PDV).
          </div>
          <Button
            onClick={() => setOpenCazar(true)}
            data-testid="open-cazar-factura"
            className="bg-violet-500 hover:bg-violet-400 text-zinc-950 font-semibold whitespace-nowrap">
            <Sparkles className="w-4 h-4 mr-1.5" /> Cazar factura con IA
          </Button>
        </div>

        {/* Histórico de facturas cazadas */}
        {facturasCazadas.length > 0 && (
          <div className="mt-4 border border-zinc-800 rounded-lg overflow-hidden" data-testid="facturas-cazadas-historico">
            <div className="bg-zinc-900/60 px-3 py-2 text-xs font-semibold text-amber-300 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Facturas ya cazadas y aprobadas ({facturasCazadas.length})
            </div>
            <div className="divide-y divide-zinc-800/60">
              {facturasCazadas.map((f) => {
                const t = f.totales || {};
                const diff = t.diferencia_total || 0;
                const diffColor =
                  Math.abs(diff) < 1 ? "text-emerald-400" :
                  diff > 0 ? "text-red-400" :
                  "text-amber-300";
                return (
                  <div key={f.id} className="px-3 py-2 flex items-center gap-3 text-xs hover:bg-zinc-900/40"
                       data-testid={`fc-row-${f.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-zinc-100 font-mono">{f.numero || "(sin nº)"} <span className="text-zinc-500">·</span> <span className="text-zinc-400">{f.fecha}</span></div>
                      <div className="text-[10px] text-zinc-500">
                        {t.lineas_matcheadas || 0} línea(s) ·
                        Aprobada por <span className="text-zinc-400">{f.aprobada_por}</span> el {f.aprobada_at?.slice(0, 10)}
                        {(t.discrepancias_alto || 0) > 0 && (
                          <span className="ml-2 text-red-400">⚠ {t.discrepancias_alto} discrepancia(s)</span>
                        )}
                        {(t.sin_precio_acordado || 0) > 0 && (
                          <span className="ml-2 text-zinc-500">· {t.sin_precio_acordado} sin precio</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-zinc-100 font-mono font-semibold">{(t.total_factura || f.total || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €</div>
                      {t.total_acordado > 0 && (
                        <div className={`text-[10px] font-mono ${diffColor}`}>
                          {diff > 0 ? "+" : ""}{diff.toLocaleString("es-ES", { minimumFractionDigits: 2 })} € vs acordado
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => eliminarFactura(f.id)}
                      data-testid={`fc-delete-${f.id}`}
                      title="Eliminar este registro (desmarcar paradas)"
                      className="text-zinc-500 hover:text-red-400 h-7 w-7 p-0"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <CazarFacturaDialog
          colaborador={openCazar ? colaborador : null}
          onClose={() => setOpenCazar(false)}
          onAprobada={() => { cargarFacturasCazadas(); cargar(); }}
        />
      </DialogContent>
    </Dialog>
  );
}
